import { useUser } from "@/hooks/auth";
import { User } from "@supabase/supabase-js";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useCallback, useEffect } from "react";

type LogLevel = "info" | "warn" | "error";

export const fireLog = async (
  level: LogLevel,
  message: string,
  context: Record<string, any> = {}
): Promise<void> => {
  try {
    await fetch("/api/v1/firelog", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ level, message, context }),
    });
  } catch (err) {
    console.error("Failed to log:", err);
  }
};

const LOG_KEY_PREFIX = "daily-log-user-";

function getTodayDateKey(): string {
  return new Date().toISOString().split("T")[0]; // e.g. "2025-05-21"
}

async function logUserVisit(user: User) {
  const importantData = {
    id: user.id,
    email: user.email,
    name:
      user.user_metadata?.full_name || user.user_metadata?.name || "Unknown",
    role: user.role || "none",
    lastSignIn: user.last_sign_in_at,
    timestamp: new Date().toISOString(),
    environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
    device: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
    },
  };

  await fireLog("info", "User visit logged", importantData);
}

export function useLogger() {
  const { data: user } = useUser();
  const today = getTodayDateKey();

  const localStorageKey = `${LOG_KEY_PREFIX}${user?.id || "unknown"}`;

  const [lastLoggedDate, setLastLoggedDate] = useLocalStorage<string | null>(
    localStorageKey,
    null
  );

  const performDailyLogging = useCallback(() => {
    if (!user) return;

    Promise.all([logUserVisit(user)])
      .then(() => {
        setLastLoggedDate(today);
      })
      .catch((err) => {
        console.error("Failed to log user visit:", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today, user]);

  useEffect(() => {
    if (!user) return;

    if (lastLoggedDate !== today) {
      performDailyLogging();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, today]);
}
